import moment from 'moment'
import { Fragment, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import DraggableItemWrapper from 'tripPlanner/components/DragAndDrop/DraggableItemWrapper'
import ItineraryDroppable from 'tripPlanner/components/DragAndDrop/ItineraryDroppable'
import NonDraggableItemWrapper from 'tripPlanner/components/DragAndDrop/NonDraggableItemWrapper'
import TimelineAddButton from 'tripPlanner/components/Timeline/TimelineAddButton'
import TimelineItemPlaceholder from 'tripPlanner/components/Timeline/TimelineItemPlaceholder'
import TimelineTripItem from 'tripPlanner/components/Timeline/TimelineTripItem'
import LoadingTimelineTripItem from 'tripPlanner/components/Timeline/LoadingTimelineTripItem'
import { ViewModeContext } from 'tripPlanner/contexts/ViewModeContext'
import { isLeBooking } from 'tripPlanner/utils'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { SectionContainer } from './common'
import { partitionBy } from 'lib/array/arrayUtils'
import config from 'constants/config'
import TimelineNLPAddItem from 'tripPlanner/components/Timeline/TimelineNLPAddItem'
import type { FullTrip } from 'tripPlanner/types/common'
import DurationLineContainer from 'tripPlanner/components/Timeline/durationLineContainer'
import { findAccommodationForDay } from 'tripPlanner/components/Timeline/durationUtils'
import { VerticalSideLine } from 'tripPlanner/components/Timeline/VerticalSideLine'
import ItineraryInfoCard from 'tripPlanner/components/CleanCard/ItineraryInfoCard'
import usePinAnchorsHeight from 'tripPlanner/hooks/usePinAnchorsHeight'

const PrintOnlyNonDraggableItemWrapper = styled(NonDraggableItemWrapper)`
  display: none;

  @media print {
    display: block;
  }
`

const DayItemsContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`

interface Props {
  startItems: Array<TripItem>
  intermediaryItems?: Array<TripItem>
  endItems: Array<TripItem>
  date: moment.Moment
  trip: FullTrip
  canEdit: boolean
  draggingItemId?: string
  regeneratingDays?: Set<string>
}

function SortableItineraryItems({
  startItems,
  intermediaryItems,
  endItems,
  date,
  trip,
  canEdit,
  draggingItemId,
  regeneratingDays,
}: Props) {
  const intl = useIntl()
  const hasTimelineItemsForDay =
    startItems.length > 0 || (intermediaryItems?.length ?? 0) > 0 || endItems.length > 0

  const dateString = date.format(ISO_DATE_FORMAT)
  const isRegenerating = regeneratingDays?.has(dateString)
  const [suggestedItems, draggableStartItems] = partitionBy(startItems, i => i.isSuggested)

  const draggingItemIndex = draggableStartItems.findIndex(
    (item) => item.id === draggingItemId,
  )
  const isDraggingItemInList = draggingItemIndex !== -1

  const { viewMode } = useContext(ViewModeContext)

  const itemsToShow = isRegenerating ? draggableStartItems.slice(0, 3) : draggableStartItems

  const aiGeneratedItems = useMemo(
    () => trip.items?.filter(item => item.isAiGenerated) || [],
    [trip.items],
  )
  const maxAiItemDate = aiGeneratedItems.length > 0 ?
      moment.max(aiGeneratedItems.map(item => moment(item.startDate))) :
    null

  const isLastAiPlannedDay = maxAiItemDate && date.isSame(maxAiItemDate, 'day')
  const isAfterMaxAiItem = maxAiItemDate && date.isAfter(maxAiItemDate, 'day')
  const isAtOrBeyondAiGenerationFrontier = isLastAiPlannedDay || isAfterMaxAiItem || !maxAiItemDate

  const shouldShowLoading = trip.generationStatus === 'IN_PROGRESS' && isAtOrBeyondAiGenerationFrontier
  const accommodationForDay = findAccommodationForDay(trip.items, date)

  const shouldShowFromHotelTravel = itemsToShow.length > 0 &&
    accommodationForDay &&
    !moment(date).isSame(accommodationForDay.startDate, 'day')

  const dayRef = useRef<HTMLDivElement>(null)
  const { height: lineHeight } = usePinAnchorsHeight(dayRef)

  const lineStyle = useMemo(() => lineHeight ? { height: `${Math.ceil(lineHeight)}px` } : undefined, [lineHeight])

  return (
    <>
      {endItems.length > 0 && endItems.map((item) => (
        <ItineraryInfoCard key={item.id} intermediateItem={item} date={date} />
      ))}
      <SectionContainer direction="vertical" data-itinerarydate={dateString}>
        {suggestedItems.map((item) => (
          <TimelineTripItem key={item.id} item={item} date={date} presentationType="start" variant="itinerary" />
        ))}

        <DayItemsContainer ref={dayRef}>
          {itemsToShow.length > 1 && (
            <VerticalSideLine style={lineStyle} />
          )}

          {shouldShowFromHotelTravel && (
            <DurationLineContainer
              fromItem={accommodationForDay}
              toItem={itemsToShow[0]}
              allDayItems={trip.items}
              currentDate={date}
              fromHotel
              isLoading={isRegenerating}
            />
          )}

          {itemsToShow.map((item, itemNumber) => (
            <Fragment key={item.id}>
              <ItineraryDroppable
                id={`${dateString}:before:${item.id}`}
                disabled={isDraggingItemInList && itemNumber === draggingItemIndex + 1}
              />
              <DraggableItemWrapper
                id={item.id}
                data={{
                  lockedToDate: isLeBooking(item) ?
                    dateString :
                    undefined,
                }}
                handlesOnlyOnHover={viewMode === 'detail'}
              >
                {isRegenerating && <LoadingTimelineTripItem regenerate />}
                {!isRegenerating && <TimelineTripItem item={item} date={date} presentationType="start" variant="itinerary" />}
              </DraggableItemWrapper>
              {itemNumber < itemsToShow.length - 1 && (
                <DurationLineContainer
                  fromItem={item}
                  toItem={itemsToShow[itemNumber + 1]}
                  allDayItems={trip.items}
                  currentDate={date}
                  isLoading={isRegenerating}
                />
              )}
            </Fragment>
          ))}

          {isRegenerating && itemsToShow.length === 0 && (
            Array.from({ length: 3 }, (_, index) => (
              <NonDraggableItemWrapper key={`${dateString}:loading:${index + 1}`}>
                <LoadingTimelineTripItem regenerate />
              </NonDraggableItemWrapper>
            ))
          )}

          {itemsToShow.length > 0 && accommodationForDay && (
            <DurationLineContainer
              fromItem={itemsToShow[itemsToShow.length - 1]}
              toItem={itemsToShow[itemsToShow.length - 1]}
              allDayItems={trip.items}
              currentDate={date}
              toHotel
              isLoading={isRegenerating}
            />
          )}
        </DayItemsContainer>

        {shouldShowLoading && (
          <>
            {Array.from({
              length: itemsToShow.length < 3 ? (3 - itemsToShow.length) : 1,
            }, (_, index) => (
              <LoadingTimelineTripItem key={index} regenerate={false} />
            ))}
          </>
        )}

        <ItineraryDroppable
          id={`${dateString}:end`}
          disabled={
            isDraggingItemInList && draggingItemIndex === draggableStartItems.length - 1
          }
        />
        {!hasTimelineItemsForDay && !canEdit && (
          <NonDraggableItemWrapper>
            <TimelineItemPlaceholder
              label={intl.formatMessage({
                id: 'ITP_ww3mlP6DTq5c0Uqx',
                defaultMessage: 'Nothing planned on this day',
                description: 'Placeholder when itinerary day has no items in the itinerary',
              })}
            />
          </NonDraggableItemWrapper>
        )}
        {!hasTimelineItemsForDay && canEdit && (
          <PrintOnlyNonDraggableItemWrapper>
            <TimelineItemPlaceholder
              label={intl.formatMessage({
                id: 'ITP_ww3mlP6DTq5c0Uqx',
                defaultMessage: 'Nothing planned on this day',
                description: 'Placeholder when itinerary day has no items in the itinerary',
              })}
            />
          </PrintOnlyNonDraggableItemWrapper>
        )}
      </SectionContainer>
      {canEdit && !isRegenerating && (
        <>
          {!config.ENABLE_TRIP_ITEM_NLP && <TimelineAddButton
            tripId={trip.id}
            label={intl.formatMessage({
              id: 'YbGXBhrP6LzLPcQ0tDys',
              defaultMessage: 'Add something to this day',
              description: 'Button label to add a new item to a specific day in the trip itinerary',
            })}
            date={dateString}
          />}
          {config.ENABLE_TRIP_ITEM_NLP && <TimelineNLPAddItem trip={trip} date={dateString} />}
        </>
      )}
    </>
  )
}

export default SortableItineraryItems
